import React, { useState } from 'react';
import {
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Chip,
  TableBody,
} from '@mui/material';
import {
  getConsumableBuyBranchKey,
  getConsumableRateForBranch,
  getConsumableSellBranchKey,
} from '../Utils/Utils';
import { ConsumableItem } from 'api/consumablesApi';
import { blue, blueGrey } from '@mui/material/colors';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import { colors } from '@material-ui/core';
import TablePagination from '@mui/material/TablePagination';

interface ConsumableRatesTableComponentProps {
  consumableData?: ConsumableItem[];
  handleDetailsViewOpen: () => void;
  setSelectedItemId: (id: string | undefined) => void;
}

export const ConsumableRatesTableComponent: React.FC<
  ConsumableRatesTableComponentProps
> = ({ consumableData, handleDetailsViewOpen, setSelectedItemId }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const allBuyBranches = Array.from(
    new Set(
      consumableData?.flatMap(
        (c) => c?.buyRates?.map(getConsumableBuyBranchKey) || []
      )
    )
  )
    .filter(Boolean)
    .sort((a, b) => {
      if (a === 'Default Price') return -1;
      if (b === 'Default Price') return 1;
      return a.localeCompare(b);
    });

  const allSellBranches = Array.from(
    new Set(
      consumableData?.flatMap(
        (c) => c?.sellRates?.map(getConsumableSellBranchKey) || []
      )
    )
  )
    .filter((key) => key !== null)
    .sort((a, b) => {
      if (a === 'Default Price') return -1;
      if (b === 'Default Price') return 1;
      return a.localeCompare(b);
    });

  return (
    // <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
    <>
      <Table stickyHeader size="medium">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                position: 'sticky',
                left: 0,
                zIndex: 5,
                background: blueGrey[50],
                minWidth: 150,
                borderRight: '1px solid #ccc',
              }}
              rowSpan={2}>
              Consumable Name
            </TableCell>

            <TableCell
              sx={{
                position: 'sticky',
                left: 150,
                zIndex: 5,
                background: blueGrey[50],
                minWidth: 70,
                maxWidth: 80,
                borderRight: '1px solid #ccc',
              }}
              rowSpan={2}>
              Unit
            </TableCell>

            <TableCell
              sx={{
                background: blueGrey[100],
                color: '#000',
                position: 'sticky',
                top: 0,
                zIndex: 4,
                borderRight: '1px solid #ccc',
              }}
              align="center"
              colSpan={allBuyBranches.length}>
              Buy Rates
            </TableCell>
            <TableCell
              sx={{
                background: blue[100],
                color: '#000',
                position: 'sticky',
                top: 0,
                zIndex: 4,
                borderRight: '1px solid #ccc',
              }}
              align="center"
              colSpan={allSellBranches.length}>
              Sell Rates
            </TableCell>
          </TableRow>
          <TableRow>
            {allBuyBranches.length > 0 &&
              allBuyBranches.map((b) => (
                <TableCell
                  sx={{
                    background: blueGrey[50],
                    minWidth: 150,
                    zIndex: 4,
                    position: 'sticky',
                    top: 0,
                    borderRight: '1px solid #ccc',
                  }}
                  key={b}
                  align="center">
                  <Chip
                    label={b}
                    sx={{
                      margin: '2px',
                      color: '#000',
                      backgroundColor: blueGrey[200],
                      paddingInline: '0.5rem',
                      paddingY: '1rem',
                    }}
                    size="small"
                    icon={
                      <BusinessIcon
                        color="action"
                        sx={{ fontSize: '1rem', color: '#000' }}
                      />
                    }
                  />
                </TableCell>
              ))}
            {allBuyBranches?.length === 0 && (
              <TableCell>
                <Typography
                  variant="caption"
                  sx={{ display: 'flex', justifyContent: 'center' }}>
                  No Buy Rates Added Yet
                </Typography>
              </TableCell>
            )}
            {allSellBranches.map((b) => (
              <TableCell
                sx={{
                  background: blue[50],
                  minWidth: 150,
                  zIndex: 4,
                  position: 'sticky',
                  top: 0,
                  borderRight: '1px solid #ccc',
                }}
                key={b}
                align="center">
                {b.split('/').map((part, index) => (
                  <Chip
                    key={`${b}-${index}`}
                    label={part}
                    sx={{
                      margin: '2px',
                      color: '#000',
                      backgroundColor: blue[200],
                      paddingInline: '0.5rem',
                      paddingY: '1rem',
                    }}
                    size="small"
                    icon={
                      index === 0 ? (
                        <BusinessIcon
                          color="action"
                          sx={{ fontSize: '1rem', color: '#000' }}
                        />
                      ) : (
                        <AccountBoxIcon
                          color="action"
                          sx={{ fontSize: '1rem', color: '#000' }}
                        />
                      )
                    }
                  />
                ))}
              </TableCell>
            ))}
            {allSellBranches?.length === 0 && (
              <TableCell>
                <Typography
                  variant="caption"
                  sx={{ display: 'flex', justifyContent: 'center' }}>
                  No Sell Rates Added Yet
                </Typography>
              </TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {!consumableData || consumableData?.length === 0 ? (
            <TableCell colSpan={4}>
              <Typography variant="body2" align="center">
                No consumable data available.
              </Typography>
            </TableCell>
          ) : (
            <>
              {consumableData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => (
                  <TableRow
                    key={row?.id}
                    style={{
                      cursor:'pointer'
                    }}
                    hover
                    onClick={() => {
                      setSelectedItemId(row?.id);
                      handleDetailsViewOpen();
                    }}>
                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 2,
                        background: '#fff',
                        minWidth: 150,
                        borderRight: '1px solid #ccc',
                      }}>
                      {row?.name}
                      {row?.isDeleted && (
                        <Chip
                          variant="outlined"
                          style={{ marginLeft: 7 }}
                          size="small"
                          sx={{
                            borderColor: colors.grey[500],
                          }}
                          label={
                            <Typography
                              variant="caption"
                              sx={{ color: colors.grey[500] }}>
                              Deleted Item
                            </Typography>
                          }
                        />
                      )}
                    </TableCell>

                    <TableCell
                      sx={{
                        position: 'sticky',
                        left: 150,
                        zIndex: 2,
                        minWidth: 70,
                        maxWidth: 80,
                        background: '#fff',
                        borderRight: '1px solid #ccc',
                      }}>
                      {row.unitType}
                    </TableCell>

                    {allBuyBranches.length > 0 ? (
                      allBuyBranches.map((b) => (
                        <TableCell
                          key={b}
                          align="center"
                          sx={{ borderRight: '1px solid #ccc' }}>
                          {getConsumableRateForBranch(row, b, 'buyRate')}
                        </TableCell>
                      ))
                    ) : (
                      <TableCell
                        align="center"
                        sx={{ borderRight: '1px solid #ccc' }}>
                        -
                      </TableCell>
                    )}
                    {allSellBranches.length > 0 ? (
                      allSellBranches?.map((b) => (
                        <TableCell
                          key={b}
                          align="center"
                          sx={{ borderRight: '1px solid #ccc' }}>
                          {getConsumableRateForBranch(row, b, 'sellRate')}
                        </TableCell>
                      ))
                    ) : (
                      <TableCell
                        align="center"
                        sx={{ borderRight: '1px solid #ccc' }}>
                        -
                      </TableCell>
                    )}
                  </TableRow>
                ))}
            </>
          )}
        </TableBody>
      </Table>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25, 50]}
        component="div"
        count={consumableData?.length || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </>
  );
};
